import { apiRequest, apiRequestPaginated } from './client';
import { mapBlockedStatus, mapIdentityStatus } from './mappers';
import type { ServiceProvider } from '../types';

type ApiProvider = {
  _id: string;
  name?: string | null;
  email?: string;
  phone?: string | null;
  profilePicture?: string | null;
  country?: string | null;
  city?: string | null;
  state?: string | null;
  identityStatus?: string;
  isBlocked?: boolean;
  createdAt?: string;
  detail?: {
    experience?: string | number;
    description?: string;
    service?: string;
    category?: string;
    subCategory?: string[];
  } | null;
};

type ApiCategory = {
  _id: string;
  name: string;
  service?: { _id: string; name: string } | string | null;
  createdAt?: string;
};

type ProviderServices = {
  category: { _id: string; name: string; service?: string };
  subcategories: Array<{ _id: string; name: string; netEarnings?: number }>;
};

const locationOf = (p: ApiProvider) =>
  [p.city, p.state, p.country].filter(Boolean).join(', ');

export const mapApiProvider = (p: ApiProvider): ServiceProvider => {
  const experience = Number(p.detail?.experience);
  return {
    id: p._id,
    name: p.name?.trim() || '—',
    email: p.email || '—',
    phone: p.phone || '—',
    avatar: p.profilePicture || undefined,
    primaryCategory: p.detail?.category || '—',
    categories: p.detail?.category ? [p.detail.category] : [],
    skills: p.detail?.subCategory || [],
    experienceYears: Number.isFinite(experience) ? experience : 0,
    serviceArea: locationOf(p) || '—',
    about: p.detail?.description || '',
    address: locationOf(p) || '—',
    applicationDate: p.createdAt || '',
    verificationStatus: mapIdentityStatus(p.identityStatus),
    status: mapBlockedStatus(p.isBlocked),
    documents: [],
  };
};

export const providersApi = {
  getProviders: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    categorySearch?: string;
  }): Promise<{ providers: ServiceProvider[]; total: number }> => {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 1000),
    });
    if (params?.search?.trim()) query.set('search', params.search.trim());
    if (params?.categorySearch && params.categorySearch !== 'All') {
      query.set('categorySearch', params.categorySearch);
    }

    const { data, pagination } = await apiRequestPaginated<{ providers: ApiProvider[] }>(
      `/admin/provider/get-providers?${query.toString()}`,
      { method: 'GET', auth: true }
    );

    return {
      providers: (data.providers || []).map(mapApiProvider),
      total: pagination.totalItems || 0,
    };
  },

  getBlockedProviders: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{ providers: ServiceProvider[]; total: number }> => {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 1000),
    });
    const { data, pagination } = await apiRequestPaginated<{ providers: ApiProvider[] }>(
      `/admin/provider/get-blocked-providers?${query.toString()}`,
      { method: 'GET', auth: true }
    );
    return {
      providers: (data.providers || []).map((p) =>
        mapApiProvider({ ...p, isBlocked: true })
      ),
      total: pagination.totalItems || 0,
    };
  },

  getAllCategories: async (): Promise<ApiCategory[]> => {
    const data = await apiRequest<{ categories: ApiCategory[] }>(
      '/admin/provider/get-all-categories',
      { method: 'GET', auth: true }
    );
    return data.categories || [];
  },

  getProviderServices: async (providerID: string): Promise<ProviderServices> => {
    return apiRequest<ProviderServices>(`/admin/provider/get-provider-services/${providerID}`, {
      method: 'GET',
      auth: true,
    });
  },

  getProviderById: async (id: string): Promise<ServiceProvider> => {
    const { providers } = await providersApi.getProviders({ page: 1, limit: 1000 });
    let provider = providers.find((p) => p.id === id);
    if (!provider) {
      const blocked = await providersApi.getBlockedProviders({ page: 1, limit: 1000 });
      provider = blocked.providers.find((p) => p.id === id);
    }
    if (!provider) throw new Error('Provider not found');

    try {
      const services = await providersApi.getProviderServices(id);
      return {
        ...provider,
        primaryCategory: services.category?.name || provider.primaryCategory,
        categories: services.category?.name ? [services.category.name] : provider.categories,
        skills: (services.subcategories || []).map((s) => s.name),
      };
    } catch {
      return provider;
    }
  },

  toggleBlockProvider: async (providerID: string, isBlocked: boolean): Promise<void> => {
    await apiRequest('/admin/provider/toggle-block-provider', {
      method: 'POST',
      auth: true,
      body: { providerID, isBlocked },
    });
  },
};
